/**
 * useSpawnUsageHistory Hook
 *
 * Aggregates spawn bag consumption by strain over a rolling period.
 * Source of truth: spawn_batch_bags where status='used' with used_at timestamps.
 *
 * Note: spawn_batches is accessed via edge function (not direct PostgREST query),
 * so this hook fetches batch data via the spawn-batches edge function and
 * cross-references with used bags from spawn_batch_bags.
 */

import { useState, useEffect, useCallback } from 'react'
import { supabase } from '@/supabase-client'

/**
 * @param {number} days - Rolling window in days (default: 90)
 * @returns {Object} { usageHistory, loading, error, refetch }
 */
export function useSpawnUsageHistory(days = 90) {
  const [usageHistory, setUsageHistory] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  const fetchUsageHistory = useCallback(async () => {
    setLoading(true)
    setError(null)

    try {
      const cutoffDate = new Date()
      cutoffDate.setDate(cutoffDate.getDate() - days)
      const cutoffStr = cutoffDate.toISOString()

      // Step 1: Get used bags within the window
      const { data: usedBags, error: bagsError } = await supabase
        .from('spawn_batch_bags')
        .select('spawn_batch_id, used_at')
        .eq('status', 'used')
        .gte('used_at', cutoffStr)

      if (bagsError) throw bagsError

      if (!usedBags || usedBags.length === 0) {
        setUsageHistory([])
        return
      }

      // Step 2: Get batch data via edge function (spawn_batches isn't directly queryable)
      const { data: edgeResponse, error: edgeError } = await supabase.functions.invoke('spawn-batches', {
        body: { action: 'list' }
      })

      if (edgeError) throw edgeError

      const batches = edgeResponse?.data || edgeResponse || []
      const batchMap = new Map()
      for (const b of (Array.isArray(batches) ? batches : [])) {
        batchMap.set(b.id, b)
      }

      // Step 3: Aggregate by strain
      const strainMap = new Map()

      for (const bag of usedBags) {
        const batch = batchMap.get(bag.spawn_batch_id)
        if (!batch?.strain) continue

        const strain = batch.strain
        const bagWeightKg = batch.bag_weight_kg || 0
        const usedDate = bag.used_at?.split('T')[0]

        if (!strainMap.has(strain.id)) {
          strainMap.set(strain.id, {
            strain_id: strain.id,
            strain_name: strain.name,
            strain_code: strain.code,
            total_bags_used: 0,
            total_kg_used: 0,
            earliest_date: usedDate,
            latest_date: usedDate
          })
        }

        const entry = strainMap.get(strain.id)
        entry.total_bags_used += 1
        entry.total_kg_used += bagWeightKg
        if (usedDate && usedDate < entry.earliest_date) entry.earliest_date = usedDate
        if (usedDate && usedDate > entry.latest_date) entry.latest_date = usedDate
      }

      // Calculate avg_weekly using the full window span
      const weeks = days / 7

      const result = Array.from(strainMap.values()).map(entry => ({
        strain_id: entry.strain_id,
        strain_name: entry.strain_name,
        strain_code: entry.strain_code,
        total_bags_used: entry.total_bags_used,
        total_kg_used: Math.round(entry.total_kg_used * 10) / 10,
        avg_weekly: Math.round(entry.total_bags_used / Math.max(1, weeks) * 10) / 10,
        avg_weekly_kg: Math.round(entry.total_kg_used / Math.max(1, weeks) * 10) / 10
      }))

      result.sort((a, b) => b.total_bags_used - a.total_bags_used)

      setUsageHistory(result)
    } catch (err) {
      console.error('[useSpawnUsageHistory] Error:', err)
      setError(err.message)
      setUsageHistory([])
    } finally {
      setLoading(false)
    }
  }, [days])

  useEffect(() => {
    fetchUsageHistory()
  }, [fetchUsageHistory])

  return {
    usageHistory,
    loading,
    error,
    refetch: fetchUsageHistory
  }
}

export default useSpawnUsageHistory
